// The chart tokens: the colours, the font and the mark sizes of every chart Component.
// A chart asks for a role ("own hotel"), never for a colour ("blue"). Each value is a step of the
// stock Tailwind palette that the app uses, so a chart matches the rest of the screen.
// The app is light only, so each token has one value, for the white surface of a card.
// The values are hex strings and not CSS variables: the chart engine writes them into SVG
// attributes, and the Sync does not copy styles/ into the app.
// This file knows nothing about the data behind the roles.

// The role of a line or a bar. The role selects the colour.
// own: the hotel of the user. Always blue-600, on every chart.
// compset: the compset median or the market. Always gray-500.
// series: any other category (a channel, a region). The series slots, in order.
// other: the sum of the categories that do not get their own line or bar ("All other"). Always gray-400.
export type ChartRole = 'own' | 'compset' | 'series' | 'other';

export const CHART_COLORS = {
	own: '#2563eb', // blue-600
	compset: '#6b7280', // gray-500
	// The min–max range of the compset: the compset colour at BAND_OPACITY.
	band: '#6b7280', // gray-500
	// A value goes up or down. Direction only, not good or bad. The same steps as RateChange.
	up: '#16a34a', // green-600
	down: '#dc2626', // red-600
	// A series after the last slot. Fold the rest into "Other" before this happens.
	other: '#9ca3af', // gray-400
	// A range of categories with no data (for example the months before the first import): a light fill.
	noData: '#f3f4f6', // gray-100
	// A marker line on the category axis (for example "today").
	marker: '#6b7280', // gray-500
	surface: '#ffffff', // white: the card
	grid: '#e5e7eb', // gray-200: the hairline grid
	axisLine: '#d1d5db', // gray-300: the axis line and the crosshair
	axisText: '#6b7280', // gray-500: the tick labels (4.8:1 on white)
	textPrimary: '#111827', // gray-900
	textSecondary: '#4b5563', // gray-600
} as const;

// The categorical slots of the role 'series', in a fixed order. The order is the colour-blind
// safety: never sort, never skip, never cycle. Slot 1 (blue-600) is the role 'own'; a 'series'
// line starts at slot 2. So blue always means "your hotel". Green and red come last, because
// RoomPulse uses them for up and down, cheaper and dearer.
// Validated on 13 Oct 2026 with the dataviz palette validator (light, surface #ffffff), and
// selected from 19,744 passing orders of Tailwind steps:
// - all 8 slots, adjacent pairs: CVD ΔE 10.3, normal-vision ΔE 28.4: PASS.
// - slots 1 to 5 (your hotel and the first 4 series), ALL pairs: CVD ΔE 10.3, normal-vision ΔE 15.4:
//   PASS. So any two of these lines can cross and stay apart.
// - contrast on white: amber-500 (slot 5) is 2.15:1. A chart with 4 series or more needs the table
//   view or the tooltip as a second way to read the values.
// Slots 2 to 4 are the first three line colours of RankLineChart.
export const CHART_SLOTS: readonly string[] = [
	'#2563eb', // 1 blue-600 (own)
	'#ea580c', // 2 orange-600
	'#0891b2', // 3 cyan-600
	'#db2777', // 4 pink-600
	'#f59e0b', // 5 amber-500
	'#6d28d9', // 6 violet-700
	'#65a30d', // 7 lime-600
	'#be123c', // 8 rose-700
];

// The colour of the n-th line with the role 'series' (0 = the first one): slot 2, 3, … 8,
// then CHART_COLORS.other.
export const seriesColorOf = (index: number): string =>
	CHART_SLOTS[index + 1] ?? CHART_COLORS.other;

export const colorOfRole = (role: ChartRole, seriesIndex: number): string => {
	if (role === 'own') return CHART_COLORS.own;
	if (role === 'compset') return CHART_COLORS.compset;
	if (role === 'other') return CHART_COLORS.other;
	return seriesColorOf(seriesIndex);
};

// The font of the app (the Tailwind font-sans stack).
export const CHART_FONT = 'ui-sans-serif, system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, "Noto Sans", sans-serif';

export const CHART_MARKS = {
	lineWidth: 2,
	// A hover marker is 8 px wide with a 2 px ring in the surface colour.
	markerRadius: 4,
	markerRing: 2,
	// The fill of a band or an area: a light wash, never a solid block.
	bandOpacity: 0.14,
	gridWidth: 1,
	fontSize: 12,
} as const;

// The total height of a chart in px, with the legend and the axis labels.
export type ChartSize = 'small' | 'normal' | 'large';

export const CHART_HEIGHT: Record<ChartSize, number> = {
	small: 200,
	normal: 280,
	large: 360,
};

// The sequential scale of a Heatmap: blue-50 … blue-700. A higher value is darker. Text on the last
// 3 steps is white (contrast 4.5:1 or more), on the other steps gray-900.
export const HEAT_SCALE: readonly string[] = [
	'#eff6ff', // blue-50
	'#dbeafe', // blue-100
	'#bfdbfe', // blue-200
	'#93c5fd', // blue-300
	'#60a5fa', // blue-400
	'#3b82f6', // blue-500
	'#2563eb', // blue-600
	'#1d4ed8', // blue-700
];
export const HEAT_DARK_FROM = 5;

// The fill of a cell with no value by design (a closed month): light diagonal stripes.
export const HATCH_FILL = 'repeating-linear-gradient(135deg, #f3f4f6 0 6px, #f9fafb 6px 12px)'; // gray-100 / gray-50
